<template>
  <div class="literature-main">
    <section class="literature-hero">
      <h2>文献检索</h2>
      <p>聚合 Semantic Scholar、CrossRef、arXiv、PubMed 开放数据源；切换「中文 / 英文」检索模式，英文模式下输入中文将自动译为英文后检索。</p>
    </section>

    <div class="search-bar">
      <div class="search-lang-switch">
        <button
          type="button"
          class="lang-btn"
          :class="{ active: searchLang === 'zh' }"
          :disabled="readonly || searching"
          @click="searchLang = 'zh'">
          中文
        </button>
        <button
          type="button"
          class="lang-btn"
          :class="{ active: searchLang === 'en' }"
          :disabled="readonly || searching"
          @click="searchLang = 'en'">
          英文
        </button>
      </div>
      <input
        v-model="query"
        type="text"
        :placeholder="searchPlaceholder"
        :disabled="readonly || searching"
        @keyup.enter="handleSearch">
      <button type="button" class="primary-btn" :disabled="readonly || searching || !query.trim()" @click="handleSearch">
        {{ searching ? '检索中…' : '检索文献' }}
      </button>
    </div>
    <p v-if="englishQueryUsed" class="english-query-hint">
      <i class="bi bi-translate"></i>
      检索词已译为：<strong>{{ englishQueryUsed }}</strong>
    </p>
    <p v-if="errorMessage" class="error-text">{{ errorMessage }}</p>

    <div class="workspace">
      <div class="results-panel">
        <div class="panel-head">
          <h2>检索结果</h2>
          <div class="head-actions">
            <span v-if="allPapers.length" class="result-count">
              共 {{ allPapers.length }} 条
              <template v-if="selectedKeys.length"> · 已选 {{ selectedKeys.length }} 篇</template>
            </span>
            <button v-if="displayPapers.length" type="button" class="ghost-btn" @click="toggleSelectPage">
              {{ pageAllSelected ? '取消本页' : '全选本页' }}
            </button>
            <button v-if="selectedKeys.length" type="button" class="ghost-btn" @click="clearSelection">
              清空已选
            </button>
          </div>
        </div>

        <div v-if="searching" class="loading-box">
          <div class="loading-spinner"></div>
          <p>{{ searchingHint }}</p>
        </div>

        <div v-else-if="!allPapers.length" class="empty-box">
          <i class="bi bi-search"></i>
          <p>输入关键词开始检索，支持中英文及 DOI；仅展示近 3 年且含作者信息的文献</p>
        </div>

        <div v-else class="paper-list-wrap">
          <div class="paper-list">
            <label
              v-for="item in displayPapers"
              :key="paperKey(item)"
              class="paper-item"
              :class="{ selected: isSelected(item) }">
              <input
                type="checkbox"
                :checked="isSelected(item)"
                :disabled="readonly"
                @change="toggleSelect(item)">
              <div class="paper-body">
                <div class="paper-title">{{ item.title }}</div>
                <div class="paper-meta">
                  <span>{{ formatAuthors(item.authors) }}</span>
                  <span v-if="item.year"> · {{ item.year }}</span>
                  <span v-if="item.venue"> · {{ item.venue }}</span>
                </div>
                <div class="paper-tags">
                  <span class="tag">{{ item.docType || 'J' }}</span>
                  <span v-if="item.relevanceScore != null" class="tag tag-score">匹配 {{ formatScore(item.relevanceScore) }}</span>
                  <span v-if="item.source" class="tag tag-source">{{ item.source }}</span>
                  <span v-if="item.citationCount != null" class="tag tag-cite">引用 {{ item.citationCount }}</span>
                </div>
                <p v-if="item.abstractText" class="paper-abstract">{{ truncate(item.abstractText, 180) }}</p>
              </div>
            </label>
          </div>

          <div v-if="totalPages > 1" class="pagination-bar">
            <button
              type="button"
              class="page-btn"
              :disabled="currentPage <= 1"
              @click="goPage(currentPage - 1)">
              上一页
            </button>
            <span class="page-info">第 {{ currentPage }} / {{ totalPages }} 页</span>
            <button
              type="button"
              class="page-btn"
              :disabled="currentPage >= totalPages"
              @click="goPage(currentPage + 1)">
              下一页
            </button>
          </div>
        </div>
      </div>

      <aside class="format-panel">
        <div class="panel-head">
          <h2>参考文献格式</h2>
        </div>

        <div class="format-options">
          <label class="format-select-label">导出格式</label>
          <select v-model="formatStyle" :disabled="readonly" class="format-select">
            <option v-for="opt in formatOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
          </select>
        </div>

        <p class="format-tip">{{ formatTip }}</p>

        <div class="format-actions">
          <button
            type="button"
            class="primary-btn"
            :disabled="readonly || formatting || selectedPapers.length === 0"
            @click="handleFormat">
            {{ formatting ? '生成中…' : `生成参考文献（${selectedPapers.length}）` }}
          </button>
          <button
            type="button"
            class="ghost-btn"
            :disabled="!bibliography"
            @click="copyBibliography">
            复制到剪贴板
          </button>
        </div>

        <div v-if="copySuccess" class="copy-success">已复制</div>

        <textarea
          v-model="bibliography"
          class="bibliography-output"
          readonly
          placeholder="选择文献并点击「生成参考文献」后，结果将显示在这里…"></textarea>

        <div class="notice-box">
          <div class="notice-head"><i class="bi bi-info-circle-fill"></i><span>说明</span></div>
          <p>数据来自开放学术 API，知网/万方等数据库暂未接入。正式引用前请核对作者、刊名、卷期页等信息。</p>
        </div>
      </aside>
    </div>

    <section id="review-panel" ref="reviewPanel" class="review-panel">
      <div class="panel-head">
        <h2>文献综述</h2>
        <div class="head-actions">
          <span v-if="reviewQuotaCost" class="quota-badge">{{ reviewQuotaCost }} 积分/次</span>
          <span v-if="reviewPaperCount" class="result-count">基于 {{ reviewPaperCount }} 篇文献</span>
        </div>
      </div>
      <p class="format-tip">
        基于检索关键词与所选文献（可跨页勾选；未勾选时默认使用前 {{ defaultReviewPaperCount }} 篇），AI 生成 Markdown 格式综述并自动追加参考文献。
        生成约需 30–90 秒，每次消耗 {{ reviewQuotaCost || 1000 }} 积分。
      </p>
      <div class="review-toolbar">
        <div class="review-toolbar-left">
          <button
            type="button"
            class="review-generate-btn"
            :disabled="readonly || reviewing || !canGenerateReview"
            @click="handleReview">
            <i class="bi" :class="reviewing ? 'bi-hourglass-split' : 'bi-stars'"></i>
            <span>{{ reviewing ? '生成中…' : '生成文献综述' }}</span>
          </button>
          <span v-if="reviewSourceCount && !reviewing" class="review-toolbar-hint">
            {{ reviewSourceCount }} 篇 · {{ reviewQuotaCost || 1000 }} 积分
          </span>
        </div>
        <div class="review-toolbar-right">
          <div class="review-btn-group">
            <button
              type="button"
              class="review-tool-btn"
              :disabled="!reviewContent"
              title="复制 Markdown"
              @click="copyReview">
              <i class="bi bi-clipboard"></i>
              <span>复制</span>
            </button>
            <button
              type="button"
              class="review-tool-btn"
              :disabled="!reviewContent || exportingDocx"
              title="导出 Word"
              @click="handleExportDocx">
              <i class="bi bi-file-earmark-word"></i>
              <span>{{ exportingDocx ? '导出中…' : 'Word' }}</span>
            </button>
          </div>
          <transition name="fade">
            <span v-if="reviewCopySuccess" class="copy-success-inline">已复制</span>
          </transition>
        </div>
      </div>
      <div v-if="reviewing" class="review-loading">
        <div class="loading-spinner"></div>
        <p>AI 正在撰写综述，请稍候…</p>
      </div>
      <div
        v-else-if="reviewContent"
        class="review-markdown markdown-body"
        v-html="reviewHtml"></div>
      <div v-else class="review-placeholder">
        输入主题并检索文献后，点击「生成文献综述」…
      </div>
    </section>

    <button
      v-if="showScrollToReview"
      type="button"
      class="scroll-to-review-btn"
      @click="scrollToReview">
      <i class="bi bi-arrow-down-circle"></i>
      <span>生成文献综述</span>
    </button>
  </div>
</template>

<script>
import { marked } from 'marked'
import {
  searchLiterature,
  formatLiterature,
  getCitationStyles,
  getReviewQuotaCost,
  generateLiteratureReview,
  exportReviewDocx
} from '@/api/portal/literature'

const DEFAULT_REVIEW_PAPER_COUNT = 10
const SEARCH_FETCH_LIMIT = 80
const PAGE_SIZE = 10

export default {
  name: 'LiteratureView',
  props: {
    readonly: { type: Boolean, default: false }
  },
  data() {
    return {
      query: '',
      searchLang: 'zh',
      englishQueryUsed: '',
      allPapers: [],
      currentPage: 1,
      pageSize: PAGE_SIZE,
      selectedKeys: [],
      searching: false,
      formatting: false,
      formatStyle: 'GB7714_SEQUENTIAL',
      bibliography: '',
      reviewContent: '',
      reviewing: false,
      exportingDocx: false,
      reviewPaperCount: 0,
      reviewQuotaCost: 0,
      reviewCopySuccess: false,
      showScrollToReview: false,
      defaultReviewPaperCount: DEFAULT_REVIEW_PAPER_COUNT,
      errorMessage: '',
      copySuccess: false,
      formatOptions: [
        { value: 'GB7714_SEQUENTIAL', label: 'GB/T 7714 顺序编码制' },
        { value: 'GB7714_AUTHOR_YEAR', label: 'GB/T 7714 著者-出版年制' },
        { value: 'APA', label: 'APA 第7版' },
        { value: 'MLA', label: 'MLA 第9版' },
        { value: 'CHICAGO', label: 'Chicago' },
        { value: 'BIBTEX', label: 'BibTeX' }
      ]
    }
  },
  computed: {
    displayPapers() {
      if (!this.allPapers.length) {
        return []
      }
      const start = (this.currentPage - 1) * this.pageSize
      return this.allPapers.slice(start, start + this.pageSize)
    },
    totalPages() {
      if (!this.allPapers.length) {
        return 0
      }
      return Math.ceil(this.allPapers.length / this.pageSize)
    },
    selectedPapers() {
      return this.allPapers.filter(item => this.selectedKeys.includes(this.paperKey(item)))
    },
    pageAllSelected() {
      if (!this.displayPapers.length) {
        return false
      }
      return this.displayPapers.every(item => this.isSelected(item))
    },
    searchPlaceholder() {
      if (this.searchLang === 'en') {
        return '英文模式：可输入中文（自动译英）或英文关键词'
      }
      return '中文模式：输入科研标题、作者、关键词或 DOI'
    },
    searchingHint() {
      if (this.searchLang === 'en') {
        return '正在翻译关键词并检索英文文献，约需 30–90 秒…'
      }
      return '正在从多个数据源检索…'
    },
    formatTip() {
      const tips = {
        GB7714_SEQUENTIAL: '按 GB/T 7714-2025 顺序编码制输出，自动识别 [J]、[J/OL]、[C] 等文献类型。',
        GB7714_AUTHOR_YEAR: '按 GB/T 7714-2025 著者-出版年制输出，条目按第一作者排序。',
        APA: '按 APA 第7版格式输出，适用于心理学、教育学等社会科学领域。',
        MLA: '按 MLA 第9版格式输出，适用于人文学科。',
        CHICAGO: '按 Chicago 格式输出，适用于历史、艺术等学科。',
        BIBTEX: '输出 BibTeX 条目，可直接导入 LaTeX 参考文献管理。'
      }
      return tips[this.formatStyle] || tips.GB7714_SEQUENTIAL
    },
    canGenerateReview() {
      return this.query.trim().length >= 2 || this.allPapers.length > 0
    },
    reviewSourceCount() {
      if (this.selectedPapers.length > 0) {
        return this.selectedPapers.length
      }
      if (this.allPapers.length > 0) {
        return Math.min(this.allPapers.length, this.defaultReviewPaperCount)
      }
      return 0
    },
    reviewUsesSelection() {
      return this.selectedPapers.length > 0
    },
    reviewSourcePapers() {
      if (this.selectedPapers.length > 0) {
        return this.selectedPapers
      }
      return this.allPapers.slice(0, this.defaultReviewPaperCount)
    },
    reviewHtml() {
      if (!this.reviewContent) {
        return ''
      }
      return marked.parse(this.reviewContent, { breaks: true, gfm: true })
    }
  },
  mounted() {
    getReviewQuotaCost().then(res => {
      const cost = res.data
      if (cost != null && cost > 0) {
        this.reviewQuotaCost = cost
      } else {
        this.reviewQuotaCost = 1000
      }
    }).catch(() => {
      this.reviewQuotaCost = 1000
    })
    getCitationStyles().then(res => {
      const list = res.data || []
      if (list.length) {
        this.formatOptions = list.map(item => ({ value: item.value, label: item.label }))
      }
    }).catch(() => {})
  },
  methods: {
    paperKey(item) {
      const base = (item.doi || item.paperId || item.title || 'paper').trim().toLowerCase()
      return base
    },
    isSelected(item) {
      return this.selectedKeys.includes(this.paperKey(item))
    },
    toggleSelect(item) {
      const key = this.paperKey(item)
      const pos = this.selectedKeys.indexOf(key)
      if (pos >= 0) {
        this.selectedKeys.splice(pos, 1)
      } else {
        this.selectedKeys.push(key)
      }
    },
    toggleSelectPage() {
      if (this.pageAllSelected) {
        const pageKeys = this.displayPapers.map(item => this.paperKey(item))
        this.selectedKeys = this.selectedKeys.filter(key => !pageKeys.includes(key))
      } else {
        this.displayPapers.forEach(item => {
          const key = this.paperKey(item)
          if (!this.selectedKeys.includes(key)) {
            this.selectedKeys.push(key)
          }
        })
      }
    },
    clearSelection() {
      this.selectedKeys = []
    },
    goPage(page) {
      if (page < 1 || page > this.totalPages) {
        return
      }
      this.currentPage = page
    },
    handleSearch() {
      if (this.readonly) {
        this.$emit('require-login')
        return
      }
      const q = this.query.trim()
      if (!q) {
        return
      }
      this.errorMessage = ''
      this.searching = true
      this.bibliography = ''
      this.reviewContent = ''
      this.reviewPaperCount = 0
      this.showScrollToReview = false
      this.selectedKeys = []
      this.currentPage = 1
      this.englishQueryUsed = ''
      searchLiterature({
        query: q,
        limit: SEARCH_FETCH_LIMIT,
        searchLang: this.searchLang
      }).then(res => {
        const payload = res.data || {}
        this.allPapers = Array.isArray(payload) ? payload : (payload.papers || [])
        this.englishQueryUsed = payload.englishQuery || ''
        if (!this.allPapers.length) {
          this.errorMessage = '近 3 年内未找到相关文献，请尝试更换关键词'
        } else {
          this.showScrollToReview = true
        }
      }).catch(err => {
        this.allPapers = []
        this.errorMessage = err.message || '检索失败'
      }).finally(() => {
        this.searching = false
      })
    },
    handleFormat() {
      if (this.readonly) {
        this.$emit('require-login')
        return
      }
      if (!this.selectedPapers.length) {
        return
      }
      this.formatting = true
      this.errorMessage = ''
      formatLiterature({
        formatStyle: this.formatStyle,
        papers: this.selectedPapers
      }).then(res => {
        const data = res.data || {}
        this.bibliography = data.bibliography || (data.citations || []).join('\n')
      }).catch(err => {
        this.errorMessage = err.message || '格式化失败'
      }).finally(() => {
        this.formatting = false
      })
    },
    handleReview() {
      if (this.readonly) {
        this.$emit('require-login')
        return
      }
      const topic = this.query.trim()
      if (!topic && !this.allPapers.length) {
        this.errorMessage = '请先输入综述主题并检索文献'
        return
      }
      this.reviewing = true
      this.errorMessage = ''
      this.reviewContent = ''
      generateLiteratureReview({
        topic: topic || (this.allPapers[0] && this.allPapers[0].title) || '',
        papers: this.reviewSourcePapers,
        selectedOnly: this.reviewUsesSelection,
        citationStyle: this.formatStyle
      }).then(res => {
        const data = res.data || {}
        this.reviewContent = data.content || ''
        this.reviewPaperCount = data.paperCount || 0
        if (data.quotaCost != null) {
          this.reviewQuotaCost = data.quotaCost
        }
        if (!this.reviewContent) {
          this.errorMessage = '未生成有效综述内容'
        } else {
          this.showScrollToReview = false
          this.$emit('review-success', {
            balanceAfter: data.balanceAfter,
            quotaCost: data.quotaCost
          })
        }
      }).catch(err => {
        this.errorMessage = err.message || '综述生成失败'
      }).finally(() => {
        this.reviewing = false
      })
    },
    scrollToReview() {
      const el = this.$refs.reviewPanel
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    handleExportDocx() {
      if (!this.reviewContent || this.exportingDocx) {
        return
      }
      this.exportingDocx = true
      this.errorMessage = ''
      const topic = this.query.trim() || (this.allPapers[0] && this.allPapers[0].title) || '文献综述'
      exportReviewDocx({
        topic,
        content: this.reviewContent
      }).then(res => {
        if (res.status !== 200) {
          throw new Error('导出失败，请稍后重试')
        }
        const blob = res.data
        if (!(blob instanceof Blob) || blob.size === 0) {
          throw new Error('导出失败')
        }
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `文献综述-${topic.slice(0, 30)}.docx`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      }).catch(err => {
        this.errorMessage = (err && err.message) || 'Word 导出失败'
      }).finally(() => {
        this.exportingDocx = false
      })
    },
    copyReview() {
      if (!this.reviewContent) {
        return
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(this.reviewContent).then(() => {
          this.reviewCopySuccess = true
          setTimeout(() => { this.reviewCopySuccess = false }, 2000)
        }).catch(() => {
          this.fallbackCopyReview()
        })
      } else {
        this.fallbackCopyReview()
      }
    },
    fallbackCopyReview() {
      const ta = document.createElement('textarea')
      ta.value = this.reviewContent
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
      this.reviewCopySuccess = true
      setTimeout(() => { this.reviewCopySuccess = false }, 2000)
    },
    copyBibliography() {
      if (!this.bibliography) {
        return
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(this.bibliography).then(() => {
          this.copySuccess = true
          setTimeout(() => { this.copySuccess = false }, 2000)
        }).catch(() => {
          this.fallbackCopy()
        })
      } else {
        this.fallbackCopy()
      }
    },
    fallbackCopy() {
      const ta = document.createElement('textarea')
      ta.value = this.bibliography
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
      this.copySuccess = true
      setTimeout(() => { this.copySuccess = false }, 2000)
    },
    formatAuthors(authors) {
      if (!authors || !authors.length) {
        return '-'
      }
      if (authors.length <= 3) {
        return authors.join(', ')
      }
      const hasCjk = authors.some(name => /[\u4e00-\u9fff]/.test(name))
      const prefix = authors.slice(0, 3).join(', ')
      return hasCjk ? prefix + ', 等' : prefix
    },
    truncate(text, max) {
      if (!text) {
        return ''
      }
      return text.length > max ? text.slice(0, max) + '…' : text
    },
    formatScore(score) {
      if (score == null) {
        return '-'
      }
      return Number(score).toFixed(1)
    }
  }
}
</script>

<style scoped>
.literature-main {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.literature-hero h1 {
  margin: 0 0 8px;
  font-size: 28px;
  font-weight: 700;
  color: #111827;
}

.literature-hero p {
  margin: 0;
  color: #6b7280;
  line-height: 1.6;
}

.search-bar {
  display: flex;
  gap: 12px;
  align-items: center;
}

.search-lang-switch {
  display: inline-flex;
  flex-shrink: 0;
  padding: 3px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f9fafb;
}

.lang-btn {
  height: 36px;
  padding: 0 14px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #6b7280;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.lang-btn.active {
  background: #fff;
  color: var(--primary);
  font-weight: 600;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.lang-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.english-query-hint {
  margin: -8px 0 0;
  padding: 10px 14px;
  border-radius: 10px;
  background: #e3f2fd;
  color: #1565c0;
  font-size: 13px;
  line-height: 1.5;
}

.english-query-hint i {
  margin-right: 6px;
}

.english-query-hint strong {
  font-weight: 600;
}

.search-bar input {
  flex: 1;
  height: 44px;
  padding: 0 16px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-size: 15px;
  background: #fff;
}

.primary-btn {
  height: 44px;
  padding: 0 20px;
  border-radius: 10px;
  background: var(--primary);
  color: #fff;
  font-size: 15px;
  font-weight: 500;
  white-space: nowrap;
}

.primary-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.ghost-btn {
  height: 36px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #374151;
  font-size: 14px;
}

.error-text {
  margin: 0;
  color: #dc2626;
  font-size: 14px;
}

.workspace {
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 20px;
  align-items: start;
}

.results-panel,
.format-panel,
.review-panel {
  background: #fff;
  border-radius: 14px;
  border: 1px solid #eef0f4;
  padding: 20px;
}

.review-panel {
  margin-top: 4px;
}

.review-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 12px 16px;
  margin-bottom: 16px;
  background: #f9fafb;
  border: 1px solid #eef0f4;
  border-radius: 12px;
}

.review-toolbar-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  min-width: 0;
}

.review-toolbar-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.review-generate-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 18px;
  border: none;
  border-radius: 10px;
  background: var(--primary);
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  transition: opacity 0.2s;
}

.review-generate-btn i {
  font-size: 16px;
}

.review-generate-btn:hover:not(:disabled) {
  opacity: 0.92;
}

.review-generate-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.review-toolbar-hint {
  font-size: 13px;
  color: #6b7280;
  white-space: nowrap;
}

.review-btn-group {
  display: inline-flex;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
}

.review-tool-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 14px;
  border: none;
  border-right: 1px solid #e5e7eb;
  background: #fff;
  color: #374151;
  font-size: 13px;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.15s;
}

.review-tool-btn:last-child {
  border-right: none;
}

.review-tool-btn:hover:not(:disabled) {
  background: #f3f4f6;
  color: var(--primary);
}

.review-tool-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.review-tool-btn i {
  font-size: 15px;
}

.copy-success-inline {
  font-size: 13px;
  color: #059669;
  white-space: nowrap;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.quota-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  background: #fff7ed;
  color: #ea580c;
}

.review-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: #6b7280;
}

.review-placeholder {
  min-height: 200px;
  padding: 16px;
  border: 1px dashed #e5e7eb;
  border-radius: 10px;
  color: #9ca3af;
  font-size: 14px;
  line-height: 1.6;
}

.review-markdown {
  min-height: 280px;
  max-height: 720px;
  overflow-y: auto;
  padding: 20px 24px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fafafa;
  font-size: 15px;
  line-height: 1.8;
  color: #111827;
}

.review-markdown >>> h1 {
  margin: 0 0 16px;
  font-size: 22px;
  font-weight: 700;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 8px;
}

.review-markdown >>> h2 {
  margin: 24px 0 12px;
  font-size: 18px;
  font-weight: 600;
}

.review-markdown >>> h3 {
  margin: 20px 0 10px;
  font-size: 16px;
  font-weight: 600;
}

.review-markdown >>> p {
  margin: 0 0 12px;
}

.review-markdown >>> ul,
.review-markdown >>> ol {
  margin: 0 0 12px;
  padding-left: 24px;
}

.review-markdown >>> li {
  margin-bottom: 6px;
}

.review-markdown >>> strong {
  font-weight: 600;
}

.review-markdown >>> blockquote {
  margin: 12px 0;
  padding: 8px 16px;
  border-left: 4px solid var(--primary);
  background: #f3f4f6;
  color: #4b5563;
}

.scroll-to-review-btn {
  position: fixed;
  right: 32px;
  bottom: 48px;
  z-index: 100;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border: none;
  border-radius: 999px;
  background: var(--primary);
  color: #fff;
  font-size: 15px;
  font-weight: 500;
  box-shadow: 0 8px 24px rgba(25, 118, 210, 0.35);
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
}

.scroll-to-review-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 28px rgba(25, 118, 210, 0.45);
}

.scroll-to-review-btn i {
  font-size: 18px;
}

.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.panel-head h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.head-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.result-count {
  font-size: 13px;
  color: #6b7280;
}

.loading-box,
.empty-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 20px;
  color: #9ca3af;
  text-align: center;
}

.empty-box i {
  font-size: 36px;
  margin-bottom: 12px;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e5e7eb;
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 12px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.paper-list-wrap {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.paper-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 620px;
  overflow-y: auto;
}

.pagination-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding-top: 4px;
}

.page-btn {
  height: 34px;
  padding: 0 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  color: #374151;
  font-size: 13px;
  cursor: pointer;
}

.page-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.page-info {
  font-size: 13px;
  color: #6b7280;
  min-width: 96px;
  text-align: center;
}

.paper-item {
  display: flex;
  gap: 12px;
  padding: 14px;
  border: 1px solid #eef0f4;
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
}

.paper-item:hover {
  border-color: rgba(25, 118, 210, 0.3);
}

.paper-item.selected {
  border-color: var(--primary);
  background: rgba(25, 118, 210, 0.04);
}

.paper-item input {
  margin-top: 4px;
  flex-shrink: 0;
}

.paper-title {
  font-size: 15px;
  font-weight: 600;
  color: #111827;
  line-height: 1.5;
}

.paper-meta {
  margin-top: 6px;
  font-size: 13px;
  color: #6b7280;
}

.paper-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 12px;
  background: #f3f4f6;
  color: #4b5563;
}

.tag-source {
  background: #e3f2fd;
  color: var(--primary);
}

.tag-cite {
  background: #ecfdf5;
  color: #059669;
}

.tag-score {
  background: #fff7ed;
  color: #ea580c;
}

.paper-abstract {
  margin: 8px 0 0;
  font-size: 13px;
  color: #9ca3af;
  line-height: 1.5;
}

.format-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
}

.format-select-label {
  font-size: 13px;
  color: #6b7280;
}

.format-select {
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  background: #fff;
}

.format-chip {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
}

.format-chip.active {
  border-color: var(--primary);
  background: rgba(25, 118, 210, 0.06);
  color: var(--primary);
}

.format-tip {
  margin: 0 0 16px;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
}

.format-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
}

.bibliography-output {
  width: 100%;
  min-height: 220px;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-size: 13px;
  line-height: 1.7;
  resize: vertical;
  font-family: inherit;
  margin-bottom: 16px;
}

.copy-success {
  margin-bottom: 8px;
  font-size: 13px;
  color: #059669;
}

.notice-box {
  padding: 14px;
  border-radius: 10px;
  background: #f9fafb;
  border: 1px solid #eef0f4;
}

.notice-head {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #374151;
}

.notice-head i {
  color: var(--primary);
}

.notice-box p {
  margin: 0;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.6;
}

@media (max-width: 960px) {
  .workspace {
    grid-template-columns: 1fr;
  }

  .search-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .search-lang-switch {
    width: 100%;
    justify-content: center;
  }

  .lang-btn {
    flex: 1;
  }

  .scroll-to-review-btn {
    right: 16px;
    bottom: 24px;
    padding: 10px 16px;
    font-size: 14px;
  }

  .review-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .review-toolbar-left,
  .review-toolbar-right {
    justify-content: space-between;
    width: 100%;
  }

  .review-btn-group {
    flex: 1;
  }

  .review-tool-btn {
    flex: 1;
    justify-content: center;
  }
}
</style>
